import { ARCHIVED_BANNER_HEIGHT } from "components/archived-project-banner";
import { CheckCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Spin, Tooltip } from "antd";
import { ColDef, ColGroupDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import CiqAgSearch from "components/ciq-ag-search";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import CiqLogPageHeader from "components/ciq-log-page-header";
import { useMemo, useRef, useState } from "react";
import { useHistory } from "react-router";
import { projectPermissionsConfig } from "pages/project-setting-menu/project-permissions";
import { TAccessLevel, TProjectPermissionRow } from "./types";
import { usePermissionRoles } from "./use-permission-roles";
import "./index.scss";

type PermissionsMatrixProps = {
  onBack?: () => void;
};

type TPermissionCellParams = {
  value: TAccessLevel;
  data?: TProjectPermissionRow;
};

type TActionCellParams = {
  value?: string;
};

type TPermissionGroupInnerHeaderProps = {
  displayName?: string;
  tooltipText?: string;
};

type TGroupRowInnerRendererParams = {
  value?: string;
};

const GROUP_HEADER_TOOLTIP_TEXT =
  "Access limited to Submittals and Materials assigned to their company.";

function PermissionGroupInnerHeader(props: TPermissionGroupInnerHeaderProps) {
  const { displayName, tooltipText } = props;
  return (
    <div className="w-full h-full flex items-center justify-center gap-1 text-center">
      <span className="permissions-matrix__group-header-text">
        {displayName}
      </span>
      {tooltipText ? (
        <Tooltip title={<span className="text-xs">{tooltipText}</span>}>
          <span className="inline-flex items-center mt-px">
            <InfoCircleOutlined className="text-[var(--ciq-black-a45)] hover:text-[var(--ciq-black-a45)]" />
          </span>
        </Tooltip>
      ) : null}
    </div>
  );
}

function PermissionsMatrix({ onBack }: PermissionsMatrixProps) {
  const history = useHistory();
  const { rows, featureGroupMetaByKey = {} } = projectPermissionsConfig;
  const { roles: fetchedRoles, loading: rolesLoading } = usePermissionRoles({
    "component-location": "project-level"
  });
  const gridRef = useRef<AgGridReact<TProjectPermissionRow>>(null);
  const [isGridReady, setIsGridReady] = useState(false);
  const headerItems: JSX.Element[] = [
    <CiqAgSearch
      key="PermissionMatrix_Search"
      gridRef={gridRef}
      placeholder="Search permission"
      disabled={!isGridReady || rolesLoading}
    />
  ];

  const gridRoles = fetchedRoles;

  const getAccessBadge = (value: TAccessLevel) => {
    if (value === "ALLOW" || value === "LIMITED")
      return (
        <div className="permissions-matrix__access-icon-cell">
          <CheckCircleOutlined
            style={{ fontSize: 18 }}
            className="text-ciq-green-600 hover:text-ciq-green-600"
          />
        </div>
      );

    return "";
  };

  const columnDefs = useMemo<
    (ColDef<TProjectPermissionRow> | ColGroupDef<TProjectPermissionRow>)[]
  >(() => {
    const roleByKey = new Map(gridRoles.map((role) => [role.key, role]));

    const buildRoleColumn = (
      roleKey: string
    ): ColDef<TProjectPermissionRow> | null => {
      const role = roleByKey.get(roleKey);
      if (!role) return null;

      return {
        field: role.key,
        headerName: role.label,
        headerTooltip: role.label,
        flex: 1,
        minWidth: 130,
        sortable: false,
        filter: false,
        headerClass: "permission-matrix-center-header",
        cellClass: "text-center",
        cellRenderer: (params: TPermissionCellParams) =>
          getAccessBadge(params.value)
      };
    };

    const buildRoleGroup = (
      headerName: string,
      roleKeys: string[],
      tooltipText?: string
    ): ColGroupDef<TProjectPermissionRow> | null => {
      const children = roleKeys
        .map((roleKey) => buildRoleColumn(roleKey))
        .filter((column): column is ColDef<TProjectPermissionRow> =>
          Boolean(column)
        );

      if (children.length === 0) return null;

      return {
        headerName,
        headerTooltip: headerName,
        headerClass: "permissions-matrix__group-header-cell",
        headerGroupComponentParams: {
          innerHeaderGroupComponent: PermissionGroupInnerHeader,
          tooltipText
        },
        children
      };
    };

    const groupedColumns = [
      buildRoleGroup("General Contractor", [
        "gc_project_admin",
        "gc_estimator",
        "gc_foreman"
      ]),
      buildRoleGroup(
        "Subcontractor",
        ["gc_sc_project_admin", "gc_sc_foreman"],
        GROUP_HEADER_TOOLTIP_TEXT
      )
    ].filter((column): column is ColGroupDef<TProjectPermissionRow> =>
      Boolean(column)
    );

    const standaloneColumns = [
      "gc_sc_architect",
      "gc_sc_design_engineer",
      "gc_sc_owner"
    ]
      .map((roleKey) => buildRoleColumn(roleKey))
      .filter((column): column is ColDef<TProjectPermissionRow> =>
        Boolean(column)
      );

    return [
      {
        field: "feature_key",
        rowGroup: true,
        hide: true
      },
      {
        field: "action",
        headerName: "Permissions",
        headerTooltip: "Permissions",
        minWidth: 350,
        flex: 2,
        sortable: false,
        cellRenderer: (params: TActionCellParams) => (
          <div className="permissions-matrix__action-text">
            <TruncatedTextWithTooltip text={params.value ?? ""} />
          </div>
        ),
        cellStyle: (params) =>
          params.node.group
            ? undefined
            : {
                paddingLeft: `${(params.node.level + 1) * 20}px`
              }
      },
      ...groupedColumns,
      ...standaloneColumns
    ];
  }, [gridRoles]);

  const defaultColDef = useMemo<ColDef<TProjectPermissionRow>>(
    () => ({
      editable: false,
      resizable: true,
      suppressMovable: true,
      suppressHeaderMenuButton: true
    }),
    []
  );

  const groupRowRendererParams = useMemo(
    () => ({
      suppressCount: true,
      innerRenderer: (params: TGroupRowInnerRendererParams) => {
        const featureKey = params.value ?? "";
        const featureMeta = featureGroupMetaByKey[featureKey];
        const displayName = featureMeta?.label || featureKey;
        const tooltipText = featureMeta?.tooltip;

        return (
          <div className="flex items-center gap-1">
            <span>{displayName}</span>
            {tooltipText ? (
              <Tooltip title={<span className="text-xs">{tooltipText}</span>}>
                <span className="inline-flex items-center mt-px">
                  <InfoCircleOutlined className="text-[var(--ciq-black-a45)] hover:text-[var(--ciq-black-a45)]" />
                </span>
              </Tooltip>
            ) : null}
          </div>
        );
      }
    }),
    [featureGroupMetaByKey]
  );

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    history.goBack();
  };

  return (
    <div
      className="permissions-matrix flex flex-col"
      style={{ height: `calc(100vh - 140px - ${ARCHIVED_BANNER_HEIGHT})` }}
    >
      <CiqLogPageHeader
        {...{
          entityName: "Permission Matrix",
          backButton: handleBack,
          items: headerItems
        }}
      />
      <div className="grow ag-theme-alpine pb-4">
        {rolesLoading ? (
          <div className="w-full h-full min-h-[240px] flex items-center justify-center">
            <Spin />
          </div>
        ) : (
          <AgGridReact<TProjectPermissionRow>
            ref={gridRef}
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            getRowClass={(params) =>
              params.node.group
                ? "permissions-matrix__row-group-parent font-semibold"
                : ""
            }
            groupDisplayType="groupRows"
            groupRowRendererParams={groupRowRendererParams}
            groupDefaultExpanded={-1}
            suppressAggFuncInHeader
            suppressContextMenu
            animateRows={false}
            columnHoverHighlight
            tooltipShowMode="whenTruncated"
            suppressDragLeaveHidesColumns
            onGridReady={() => {
              setIsGridReady(true);
              gridRef.current?.api?.sizeColumnsToFit();
            }}
          />
        )}
      </div>
    </div>
  );
}

export default PermissionsMatrix;
